

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios"; // Add this import for the exchange rate API
import {
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, 
  IconButton, Autocomplete, Grid, Typography, Select, MenuItem, InputLabel, FormControl, 
  Chip, OutlinedInput, Box, Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { v4 as uuidv4 } from "uuid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from '../../auth/Auth';
import InfoIcon from '@mui/icons-material/Info';
import Popper from '@mui/material/Popper';
import { InputAdornment } from "@mui/material";
import colors from "assets/theme/base/colors";
import { useCurrency } from '../../context/CurrencyContext';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'; // Add this for the conversion icon

function QuotationModal({
  isOpen,
  onClose,
  onSave,
  dealId, // Make sure this prop is passed
  dealName,
  onProductsChange,
  user,
  resetForm,
}) {
  const { id } = useParams();
  const { axiosInstance } = useAuth();
  const { getCurrencySymbol, formatCurrency } = useCurrency();
  
  // Original states
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quoteCurrency, setQuoteCurrency] = useState('INR');
  const [quotationStatus, setQuotationStatus] = useState('Draft');
  const [totalAmount, setTotalAmount] = useState(0);
  const [tax, setTax] = useState(0);
  const [shippingMethod, setShippingMethod] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [productList, setProductList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [subtotal, setSubtotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [taxSummary, setTaxSummary] = useState({});
  const [dealInput, setDealInput] = useState('');
  const [rows, setRows] = useState([
    {
      id: uuidv4(),
      productName: "",
      price: 0,
      tax: 0,
      quantity: 0,
      discount_percentage: 0,
      discountPrice: 0,
      totalPrice: 0,
    },
  ]);
  const [suggestions, setSuggestions] = useState({
    locations: [],
    categories: [],
    brands: [],
    manufacturers: [],
    names: [],
  });
  const [existingQuoteNames, setExistingQuoteNames] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // Add severity state
  const [leadData, setLeadData] = useState(null);
  const [dealData, setDealData] = useState(null);

  // New states for currency conversion
  const [exchangeRates, setExchangeRates] = useState({});
  const [showCurrencyConversionDialog, setShowCurrencyConversionDialog] = useState(false);
  const [currentConversion, setCurrentConversion] = useState({
    rowId: null,
    productId: null,
    productName: '',
    sourceCurrency: '',
    targetCurrency: '',
    originalPrice: 0,
    convertedPrice: 0,
    exchangeRate: 1,
  });
  const [isLoadingRates, setIsLoadingRates] = useState(false);

  // Function to fetch exchange rates
  const fetchExchangeRate = async (sourceCurrency, targetCurrency) => {
    if (sourceCurrency === targetCurrency) {
      return 1; // Same currency, no conversion needed
    }

    // Check if we already have this rate
    const rateKey = `${sourceCurrency}_${targetCurrency}`;
    if (exchangeRates[rateKey]) {
      return exchangeRates[rateKey];
    }

    setIsLoadingRates(true);
    try {
      const response = await axios.get(`https://open.er-api.com/v6/latest/${sourceCurrency}`);
      const rates = response.data.rates;
      const rate = rates[targetCurrency];
      
      // Store all rates from this response
      const newRates = { ...exchangeRates };
      Object.keys(rates).forEach(curr => {
        newRates[`${sourceCurrency}_${curr}`] = rates[curr];
      });
      setExchangeRates(newRates);
      
      setIsLoadingRates(false);
      return rate;
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      setIsLoadingRates(false);
      return 1; // Default to 1:1 if API fails
    }
  };

  // Modified function to get deal's currency
  const getDealCurrency = async (dealId) => {
    try {
      // First fetch the deal details
      const { data: deal } = await axiosInstance.get(`/deals/${dealId}`);
      
      if (!deal) {
        throw new Error('Deal not found');
      }
      
      // If the deal already has a currency, use it directly
      if (deal.currency) {
        return {
          currencyCode: deal.currency,
          currencySymbol: getCurrencySymbol(deal.currency)
        };
      }
      
      // Check if deal has a lead_id
      if (deal.lead_id) {
        // Fetch lead details to get currency
        const { data: lead } = await axiosInstance.get(`/leads/${deal.lead_id}`);
        if (lead && lead.currency) {
          return {
            currencyCode: lead.currency,
            currencySymbol: getCurrencySymbol(lead.currency)
          };
        }
      }
      
      // If no lead or lead has no currency, use account currency
      if (deal.account_id) {
        const { data: account } = await axiosInstance.get(`/accounts/${deal.account_id}`);
        if (account && account.currency) {
          return {
            currencyCode: account.currency,
            currencySymbol: getCurrencySymbol(account.currency)
          };
        }
      }
      
      // Fall back to default currency if nothing found
      return {
        currencyCode: 'INR', // Default currency for quotation
        currencySymbol: getCurrencySymbol('INR')
      };
    } catch (error) {
      console.error('Error fetching deal currency:', error);
      // Return default currency in case of error
      return {
        currencyCode: 'INR',
        currencySymbol: getCurrencySymbol('INR')
      };
    }
  };

  // Set the currency based on the deal
  useEffect(() => {
    const dealIdToUse = dealId || id; // Use passed dealId or from params
    if (dealIdToUse) {
      const fetchDealCurrency = async () => {
        try {
          const { currencyCode } = await getDealCurrency(dealIdToUse);
          if (currencyCode) {
            setQuoteCurrency(currencyCode);
          }
        } catch (error) {
          console.error('Error fetching deal currency:', error);
        }
      };
      
      fetchDealCurrency();
    }
  }, [dealId, id]);

  // Modified handleProductSelect to check for currency differences
  const handleProductSelect = async (rowId, selectedProduct) => {
    if (!selectedProduct) {
      // If no product is selected, reset the row data
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id === rowId) {
            return {
              ...row,
              productName: "",
              productId: null,
              price: 0,
              tax: 0,
              quantity: 0,
              discount_percentage: 0,
              discountPrice: 0,
              totalPrice: 0,
            };
          }
          return row;
        })
      );
      return;
    }

    // Find the product in the productList
    const product = productList.find((p) => p.id === selectedProduct.id);
    
    if (!product) {
      console.error('Product not found:', selectedProduct);
      return;
    }

    // Check if product has a different currency than quote
    const productCurrency = product.currency || 'INR';
    
    if (productCurrency !== quoteCurrency) {
      // Currency conversion needed
      try {
        const exchangeRate = await fetchExchangeRate(productCurrency, quoteCurrency);
        const originalPrice = product.price || 0;
        const convertedPrice = originalPrice * exchangeRate;
        
        // Show currency conversion dialog
        setCurrentConversion({
          rowId,
          productId: product.id,
          productName: product.name,
          sourceCurrency: productCurrency,
          targetCurrency: quoteCurrency,
          originalPrice,
          convertedPrice,
          exchangeRate,
        });
        
        setShowCurrencyConversionDialog(true);
      } catch (error) {
        console.error('Error during currency conversion:', error);
        // Fall back to direct conversion without dialog
        updateRowWithProduct(rowId, product, 1);
      }
    } else {
      // No currency conversion needed, update row directly
      updateRowWithProduct(rowId, product);
    }
  };

  // Helper function to update a row with product data
  const updateRowWithProduct = (rowId, product, customExchangeRate = null) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          let price = product.price || 0;
          const productCurrency = product.currency || 'INR';
          
          // Apply exchange rate if needed
          if (productCurrency !== quoteCurrency && customExchangeRate !== null) {
            price = price * customExchangeRate;
          }
          
          const taxRate = product.tax_rate || 0;
          const quantity = 0; // Default quantity
          const discount_percentage = 0; // Default discount
          
          const discountPrice = (price * (1 - discount_percentage / 100)).toFixed(2);
          const totalPrice = (parseFloat(discountPrice)) * quantity;
          
          return {
            ...row,
            productName: product.name || "",
            productId: product.id || null,
            productCurrency: productCurrency, // Store original currency
            originalPrice: product.price || 0, // Store original price
            price,
            tax: taxRate,
            quantity,
            discount_percentage,
            discountPrice,
            totalPrice: totalPrice.toFixed(2),
            convertedCurrency: productCurrency !== quoteCurrency,
            exchangeRate: customExchangeRate,
          };
        }
        return row;
      })
    );
  };

  // Handle confirmation of currency conversion
  const handleConfirmConversion = (customRate = null) => {
    const { rowId, productId } = currentConversion;
    const product = productList.find(p => p.id === productId);
    
    if (!product) {
      console.error('Product not found for conversion');
      setShowCurrencyConversionDialog(false);
      return;
    }
    
    // Use custom rate if provided, otherwise use the calculated rate
    const rateToUse = customRate !== null ? customRate : currentConversion.exchangeRate;
    
    // Update the row with the converted price
    updateRowWithProduct(rowId, product, rateToUse);
    
    // Show success message
    setSnackbarMessage(`Converted ${product.name} price from ${currentConversion.sourceCurrency} to ${currentConversion.targetCurrency}`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    
    // Close the dialog
    setShowCurrencyConversionDialog(false);
  };

  // Handle cancellation of currency conversion
  const handleCancelConversion = () => {
    setShowCurrencyConversionDialog(false);
    // Reset the current row
    const { rowId } = currentConversion;
    setRows(prevRows => 
      prevRows.map(row => 
        row.id === rowId 
          ? {
              ...row,
              productName: "",
              productId: null,
              price: 0,
              tax: 0,
              quantity: 0,
              discount_percentage: 0,
              discountPrice: 0,
              totalPrice: 0,
            }
          : row
      )
    );
  };

  // Handle custom rate input change
  const handleExchangeRateChange = (e) => {
    const newRate = parseFloat(e.target.value);
    if (!isNaN(newRate) && newRate > 0) {
      setCurrentConversion(prev => ({
        ...prev,
        exchangeRate: newRate,
        convertedPrice: prev.originalPrice * newRate
      }));
    }
  };

  useEffect(() => {
    if (!isOpen || resetForm) {
      setQuotationStatus('Draft');
      setTotalAmount(0);
      setTax(0);
      setShippingMethod('');
      setPaymentMethods([]);
      setNotes('');
      setDeliveryDate('');

      // Only reset these rows, not the dealInput
      setRows([
        {
          id: uuidv4(),
          productName: "",
          price: 0,
          tax: 0,
          quantity: 0,
          discount_percentage: 0,
          discountPrice: 0,
          totalPrice: 0,
        },
      ]);

      // If you want to regenerate the quote name each time, you can do:
      if (isOpen) {
        const fetchExistingQuotes = async () => {
          try {
            const response = await axiosInstance.get('/quotations/');
            const quotes = response.data.map(quote => quote.quote_name);
            const newUniqueName = generateUniqueQuoteName(quotes);
            setDealInput(newUniqueName);
          } catch (error) {
            console.error("Error fetching existing quotes:", error);
            const fallbackName = `Quote-${String(Date.now()).slice(-4)}`;
            setDealInput(fallbackName);
          }
        };

        fetchExistingQuotes();
      }
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (user) {
      const currentUserAsAgent = {
        id: user.id,
        username: user.username
      };
      setSelectedAgent(currentUserAsAgent);
    }
  }, [user]);

  // Function to generate a unique quote name
  const generateUniqueQuoteName = (quotations) => {
    // Print all quotation details
    // console.log('========== QUOTE NAME GENERATION START ==========');
    // console.log('ALL QUOTATIONS:');
    quotations.forEach((quote, index) => {
      // console.log(`${index + 1}. Name: ${quote.quote_name}, Created: ${quote.created_at}`);
    });

    // Filter quotes starting with Quote-
    const validQuotes = quotations
      .filter(quote => quote.quote_name.startsWith('Quote-'))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // console.log('\nQUOTES STARTING WITH "Quote-" (sorted by date):');
    validQuotes.forEach((quote, index) => {
      // console.log(`${index + 1}. Name: ${quote.quote_name}, Created: ${quote.created_at}`);
    });

    if (validQuotes.length === 0) {
      // console.log('\nNo valid quotes found, starting with Quote-0001');
      // console.log('========== QUOTE NAME GENERATION END ==========\n');
      return 'Quote-0001';
    }

    // Get the most recently created quote
    const lastQuote = validQuotes[0];
    // console.log('\nMOST RECENT QUOTE:');
    // console.log(`Name: ${lastQuote.quote_name}`);
    // console.log(`Created: ${lastQuote.created_at}`);

    // Extract number from the quote name
    const match = lastQuote.quote_name.match(/Quote-(\d+)/);
    if (!match) {
      // console.log('\nNo number pattern found in last quote, starting with Quote-0001');
      // console.log('========== QUOTE NAME GENERATION END ==========\n');
      return 'Quote-0001';
    }

    // Increment the number
    const lastNumber = parseInt(match[1], 10);
    const nextNumber = lastNumber + 1;
    const newQuoteName = `Quote-${String(nextNumber).padStart(4, '0')}`;
    
    // console.log('\nNEW QUOTE NAME GENERATION:');
    // console.log(`Last Number: ${lastNumber}`);
    // console.log(`Next Number: ${nextNumber}`);
    // console.log(`New Quote Name: ${newQuoteName}`);
    // console.log('========== QUOTE NAME GENERATION END ==========\n');
    
    return newQuoteName;
  };

  // Fetch existing quote names
  useEffect(() => {
    const fetchExistingQuotes = async () => {
      try {
        console.log('========== FETCHING QUOTATIONS START ==========');
        const response = await axiosInstance.get('/quotations/');
        
        console.log('API RESPONSE DATA:');
        console.log('Total quotations:', response.data.length);
        console.log('Raw quotation names:');
        response.data.forEach((quote, index) => {
          console.log(`${index + 1}. ${quote.quote_name}`);
        });

        const quotations = response.data;
        const newUniqueName = generateUniqueQuoteName(quotations);
        
        console.log('\nFINAL RESULTS:');
        console.log('New Quote Name Generated:', newUniqueName);
        console.log('Existing Names Count:', quotations.length);
        console.log('========== FETCHING QUOTATIONS END ==========\n');
        
        setExistingQuoteNames(quotations.map(quote => quote.quote_name));
        setDealInput(newUniqueName);
      } catch (error) {
        console.error('========== ERROR FETCHING QUOTATIONS ==========');
        console.error('Error details:', error);
        console.log('Using fallback name: Quote-0001');
        console.log('========== ERROR HANDLING END ==========\n');
        setDealInput('Quote-0001');
        setExistingQuoteNames([]);
      }
    };

    fetchExistingQuotes();
  }, []);

  const handleSaveQuotation = () => {
    try {
      console.log('Starting save quotation with name:', dealInput);
      // Validate quotation items first
      if (!rows || rows.length === 0) {
        setSnackbarMessage("Cannot create quotation without items");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Check if any row has empty or invalid data
      const invalidRow = rows.find(row =>
        !row.productName ||
        !row.quantity ||
        row.quantity <= 0 ||
        !row.price ||
        row.price <= 0
      );

      if (invalidRow) {
        setSnackbarMessage("Please fill in all required fields for each item (Product, Quantity, and Price)");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Check for unique quote name
      if (existingQuoteNames.includes(dealInput)) {
        setSnackbarMessage("Quote name must be unique. Please choose a different name.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const updatedProducts = rows.map((row) => {
        const product = productList.find((product) => product.id === row.productId);

        if (!product) {
          throw new Error(`Product not found for row: ${row.productName}`);
        }

        return {
          id: row.id,
          product_id: product.id,
          product_name: row.productName,
          price: parseFloat(row.price || 0),
          original_price: row.originalPrice || row.price,
          original_currency: row.productCurrency || quoteCurrency,
          exchange_rate: row.exchangeRate || 1,
          tax: parseFloat(product.tax_rate),
          quantity: parseInt(row.quantity || 0, 10),
          discount_percentage: parseFloat(row.discount_percentage || 0),
          discounted_price: parseFloat(row.discountPrice || 0),
          total_price: parseFloat(row.totalPrice || 0),
          quote_name: dealInput,
          created_by: user?.id,
          updated_by: user?.id,
          created_by_name: user?.username,
          updated_by_name: user?.username,
        };
      });

      setSelectedProducts(updatedProducts);

      // Tax Slabs and Amounts
      const taxSummary = calculateSummary().taxSummary || [];

      if (!Array.isArray(taxSummary)) {
        console.error('Tax summary is not an array:', taxSummary);
        return;
      }

      const taxSlabs = taxSummary.map(item => item.taxRate || null); // Ensure taxRate is available
      const taxAmounts = taxSummary.map(item => item.amount || null); // Ensure amount is available

      onSave({
        quote_name: dealInput,
        status: quotationStatus,
        total_amount: calculateSummary().grandTotal,
        items: updatedProducts,
        subtotal: calculateSummary().subtotal,
        // Tax breakdown
        tax_slab_1: taxSlabs[0] || null,
        tax_slab_2: taxSlabs[1] || null,
        tax_slab_3: taxSlabs[2] || null,
        tax_slab_4: taxSlabs[3] || null,
        tax_slab_5: taxSlabs[4] || null,
        tax_slab_amount_1: taxAmounts[0] || null,
        tax_slab_amount_2: taxAmounts[1] || null,
        tax_slab_amount_3: taxAmounts[2] || null,
        tax_slab_amount_4: taxAmounts[3] || null,
        tax_slab_amount_5: taxAmounts[4] || null,
        tax: parseFloat(tax || 0),
        shipping_method: shippingMethod,
        payment_methods: paymentMethods,
        notes: notes,
        quote_date: deliveryDate
          ? new Date(deliveryDate).toISOString().split('T')[0]
          : null,
        created_by: user?.id,
        updated_by: user?.id,
        created_by_name: user?.username,
        updated_by_name: user?.username,
        currency: quoteCurrency, // Add the currency to the saved data
      });
      onClose();
    } catch (error) {
      console.error('Error in handleSaveQuotation:', error);
      setSnackbarMessage(error.message || "Error creating quotation");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    setOpenConfirmationDialog(true);
  };

  const handleConfirmCancel = () => {
    setOpenConfirmationDialog(false);
    onClose();
  };

  const handleDialogClose = () => {
    setOpenConfirmationDialog(false);
  };

  const handleInputChange = (id, field, value) => {
    if (field === "discount_percentage") {
      if (value > 100) {
        value = 100;
      } else if (value < 0) {
        value = 0;
      }
    }

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };

          if (field === "quantity" || field === "discount_percentage") {
            const discountPrice = (
              updatedRow.price * (1 - updatedRow.discount_percentage / 100)
            ).toFixed(2);

            const totalPrice = (
              parseFloat(discountPrice) * updatedRow.quantity
            ).toFixed(2);

            updatedRow.discountPrice = discountPrice;
            updatedRow.totalPrice = totalPrice;
          }

          return updatedRow;
        }
        return row;
      })
    );
  };

  useEffect(() => {
    const extractUniqueSuggestions = async () => {
      try {
        const [productsResponse, inventoryResponse] = await Promise.all([
          axiosInstance.get("/products/"),
          axiosInstance.get("/inventory/details/"),
        ]);

        const extractUnique = (arr, field) =>
          [...new Set(arr.map((item) => ({ id: item.id, name: item[field] })))].filter(
            (value) => value.name && value.name.trim() !== ""
          );

        setSuggestions({
          locations: extractUnique(inventoryResponse.data, "location"),
          categories: extractUnique(productsResponse.data, "category"),
          brands: extractUnique(productsResponse.data, "brand"),
          manufacturers: extractUnique(productsResponse.data, "manufacturer"),
          names: extractUnique(productsResponse.data, "name"),
        });

        setProductList(productsResponse.data); // Store the products in productList
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };

    extractUniqueSuggestions();
  }, []);

  // Calculate subtotal (net), tax on discounted amount, and grand total
  const calculateSummary = () => {
    let subtotal = 0;
    let taxSummary = {};

    rows.forEach((row) => {
      const price = parseFloat(row.price) || 0;
      const quantity = parseFloat(row.quantity) || 0;
      const discountPercentage = parseFloat(row.discount_percentage) || 0;
      const taxRate = parseFloat(row.tax) || 0;
      const discountPrice = parseFloat(row.discountPrice);
      const discountedUnit = Number.isFinite(discountPrice)
        ? discountPrice
        : price * (1 - discountPercentage / 100);
      const itemNet = discountedUnit * quantity;
      subtotal += itemNet;

      if (taxRate) {
        const taxAmount = itemNet * (taxRate / 100);
        taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
      }
    });

    const grandTotal = subtotal + Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);

    const taxSummaryArray = Object.keys(taxSummary).map(taxRate => ({
      taxRate: parseFloat(taxRate),
      amount: taxSummary[taxRate],
    }));

    return { subtotal, taxSummary: taxSummaryArray, grandTotal };
  };

  const { subtotal: calculatedSubtotal, taxSummary: calculatedTaxSummary, grandTotal: calculatedGrandTotal } = calculateSummary();

  const handleRemoveRow = (id) => {
    // Prevent removal if there's only one row left
    if (rows.length > 1) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      onProductsChange(updatedRows);
    } else {
      // Optionally, show a message or alert to inform the user
      setSnackbarMessage("At least one row must remain.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        id: uuidv4(),
        productName: "",
        price: 0,
        tax: 0,
        quantity: 0,
        discount_percentage: 0,
        discountPrice: 0,
        totalPrice: 0,
      },
    ]);
  };

  const handleDateChange = (e) => {
    const isoDate = e.target.value; // ISO format from date input
    setDeliveryDate(isoDate);
  };

  const today = new Date().toISOString().split("T")[0];

  // Format the date in DD/MM/YYYY format for display
  const formattedDate = "MM/DD/YYYY*";

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Render currency conversion dialog
  const renderCurrencyConversionDialog = () => (
    <Dialog
      open={showCurrencyConversionDialog}
      onClose={handleCancelConversion}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        }
      }}
    >
      {/* Dialog Header */}
      <Box className="pearl-popup-header"
        sx={{
          backgroundColor: colors.popupHeader.background,
          px: 3,
          py: 2.5,
        }}
      >
        <MDTypography
          variant="h6"
          component="h2"
          sx={{ color: '#FFFFFF' }}
        >
          <CurrencyExchangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Currency Conversion Required
        </MDTypography>
      </Box>

      {/* Dialog Content */}
      <DialogContent sx={{ p: 3, mt: 1 }}>
        {isLoadingRates ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Fetching latest exchange rates...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              The selected product <strong>{currentConversion.productName}</strong> is priced in <strong>{currentConversion.sourceCurrency}</strong> but your quotation is in <strong>{currentConversion.targetCurrency}</strong>.
            </Typography>
            
            <Box sx={{ my: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Original Price:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(currentConversion.originalPrice, currentConversion.sourceCurrency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Converted Price:</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(currentConversion.convertedPrice, currentConversion.targetCurrency)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Typography variant="body2" gutterBottom>
              Current exchange rate: 1 {currentConversion.sourceCurrency} = {currentConversion.exchangeRate.toFixed(4)} {currentConversion.targetCurrency}
            </Typography>
            
            <TextField
              fullWidth
              label="Custom Exchange Rate (Optional)"
              type="number"
              InputProps={{
                inputProps: { min: 0.0001, step: 0.0001 }
              }}
              value={currentConversion.exchangeRate}
              onChange={handleExchangeRateChange}
              helperText="You can adjust the exchange rate if needed"
              margin="normal"
            />
          </>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <MDButton
          onClick={handleCancelConversion}
          variant="outlined"
          color="secondary"
          disabled={isLoadingRates}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={() => handleConfirmConversion(currentConversion.exchangeRate)}
          variant="gradient"
          color="info"
          disabled={isLoadingRates}
        >
          Apply Conversion
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby="quotation-modal-title"
        aria-describedby="quotation-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '90%',
            height: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="quotation-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Add New Quotation
            </MDTypography>
          </Box>

          {/* Scrollable content area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 4,
              '&::-webkit-scrollbar': {
                width: '0.4em'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555'
              }
            }}
          >
            {/* Deal Name Input */}
            <MDBox mt={4} mb={3}>
              <Grid container spacing={4} alignItems="center" justifyContent="space-between">
                {/* Name Section */}
                <Grid item>
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="h6" mr={2}>
                      Quote Name:
                    </MDTypography>
                    <TextField
                      value={dealInput} // Controlled input
                      onChange={(e) => setDealInput(e.target.value)} // Allow editing
                      variant="outlined"
                      size="medium"
                      sx={{ width: '250px' }} // Smaller width for input
                      placeholder="Enter Name"
                    />
                  </MDBox>
                </Grid>
                
                <Grid item>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="currency-select-label">Currency</InputLabel>
                    <Select
                      labelId="currency-select-label"
                      id="currency-select"
                      value={quoteCurrency}
                      onChange={(e) => setQuoteCurrency(e.target.value)}
                      label="Currency"
                    >
                      <MenuItem value="USD">USD (US Dollar)</MenuItem>
                      <MenuItem value="EUR">EUR (Euro)</MenuItem>
                      <MenuItem value="AED">AED (UAE Dirham)</MenuItem>
                      <MenuItem value="AUD">AUD (Australian Dollar)</MenuItem>
                      <MenuItem value="SGD">SGD (Singapore Dollar)</MenuItem>
                      <MenuItem value="CAD">CAD (Canadian Dollar)</MenuItem>
                      <MenuItem value="INR">INR (Indian Rupee)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </MDBox>

            {/* Product Selection Table */}
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: '20%' }}>Product Name</TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>Unit Price</TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>Tax %</TableCell>
                    <TableCell align="center" sx={{ width: '12%' }}>Discount %</TableCell>
                    <TableCell align="center" sx={{ width: '12%' }}>
                      Discounted Price
                      <Tooltip title="Discounted Price = (Unit Price - Discount %)" arrow>
                        <IconButton size="small" sx={{ marginLeft: 0.5 }}>
                          <InfoIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '12%' }}>Quantity</TableCell>
                    <TableCell align="center" sx={{ width: '12%' }}>
                      Total Net Price
                      <Tooltip title="Total Net Price = Discounted Price * Quantity" arrow>
                        <IconButton size="small" sx={{ marginLeft: 0.5 }}>
                          <InfoIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '12%' }}>Actions</TableCell>
                  </TableRow>

                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="center">
                          <Autocomplete
                            options={suggestions.names}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_, value) => handleProductSelect(row.id, value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select Product"
                                size="small"
                                sx={{ width: '90%' }}
                              />
                            )}
                            PopperComponent={(props) => (
                              <Popper
                                {...props}
                                sx={{
                                  '& .MuiAutocomplete-listbox': {
                                    maxHeight: '300px',
                                    width: '200px'
                                  }
                                }}
                              />
                            )}
                          />
                          {row.convertedCurrency && (
                            <Chip 
                              icon={<CurrencyExchangeIcon />} 
                              label="Converted" 
                              size="small" 
                              color="primary" 
                              sx={{ mt: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {formatCurrency(row.price, quoteCurrency)}
                          {row.convertedCurrency && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Original: {formatCurrency(row.originalPrice, row.productCurrency)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">{row.tax}%</TableCell>
                        <TableCell align="center">
                          <TextField
                            value={row.discount_percentage}
                            onChange={(e) => handleInputChange(row.id, "discount_percentage", Number(e.target.value))}
                            type="number"
                            placeholder="Discount"
                            size="small"
                            sx={{ width: '110%' }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">%</InputAdornment>
                              )
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">{formatCurrency(row.discountPrice, quoteCurrency)}</TableCell>
                        <TableCell align="center">
                          <TextField
                            value={row.quantity}
                            onChange={(e) => handleInputChange(row.id, "quantity", Number(e.target.value))}
                            type="number"
                            placeholder="Quantity"
                            size="small"
                            sx={{ width: '80%' }}
                          />
                        </TableCell>
                        <TableCell align="center">{formatCurrency(row.totalPrice, quoteCurrency)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <IconButton onClick={() => handleRemoveRow(row.id)}>
                              <RemoveIcon />
                            </IconButton>
                            <IconButton color="primary" onClick={handleAddRow}>
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableHead>
              </Table>
            </TableContainer>

            {/* Summary Section */}
            <Grid container spacing={2} sx={{ marginTop: "10px" }}>
              <Grid item xs={12} sx={{ display: "flex", flexDirection: "row", justifyContent: "space-evenly", ml: "20px" }}>
                <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", mt: "-10px" }}>
                  {/* Left Section: Shipping Details */}
                  <Grid item>
                    <Typography variant="h6">
                      Subtotal: {formatCurrency(calculateSummary().subtotal, quoteCurrency)}
                    </Typography>
                    {calculateSummary().taxSummary.map(({ taxRate, amount }) => (
                      <Typography key={taxRate} variant="body2">
                        Tax ({taxRate}%): {formatCurrency(amount, quoteCurrency)}
                      </Typography>
                    ))}
                  </Grid>

                  {/* Right Section: Grand Total */}
                  <Grid item>
                    <Typography variant="h6">
                      Grand Total: {formatCurrency(calculateSummary().grandTotal, quoteCurrency)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Other sections (Shipping Method, Payment Methods, etc.) */}
                <Grid item xs={12} md={6} ml='-50px' mr='50px' mt='-20px'>
                  <TextField
                    label="Shipping Method"
                    value={shippingMethod}
                    onChange={(e) => setShippingMethod(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Quote Expiration Date"
                    type="date"
                    value={deliveryDate}
                    onChange={handleDateChange}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: today, // Prevent past dates from being selected
                    }}
                    // helperText={formattedDate} // Display Indian format below the input as a hint
                  />
                </Grid>
              </Grid>
              
              {/* Notes Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <TextField
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              borderTop: '1px solid #eee',
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <MDButton
              onClick={handleCancel}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSaveQuotation}
              variant="gradient"
              color="info"
            >
              Save
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* Currency Conversion Dialog */}
      {renderCurrencyConversionDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmationDialog}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        {/* Dialog Header */}
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
          }}
        >
          <MDTypography
            variant="h6"
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Confirm Close
          </MDTypography>
        </Box>

        {/* Dialog Content */}
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <MDTypography variant="body2">
            Do you really want to close without saving?
          </MDTypography>
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            onClick={handleDialogClose}
            variant="outlined"
            color="secondary"
          >
            No
          </MDButton>
          <MDButton
            onClick={handleConfirmCancel}
            variant="gradient"
            color="info"
          >
            Yes
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default QuotationModal;